<script setup>
import { ref, computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { getVariablesForTrigger } from 'flow_builder/constants/flowVariables.js';

const { t } = useI18n();
const resolveLabel = label => label?.startsWith('FLOW_BUILDER.') ? t(label) : (label ?? '');

const props = defineProps({
  actionName: { type: String, default: '' },
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

const flowTriggerEvent = inject('flowTriggerEvent', null);
const flowDynamicVariables = inject('flowDynamicVariables', null);

const availableVariables = computed(() => {
  const base = getVariablesForTrigger(flowTriggerEvent?.value ?? null);
  const dynamic = flowDynamicVariables?.value ?? [];
  return [...base, ...dynamic];
});

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

const activeField = ref(null);
const varSearch = ref('');

const filteredVars = computed(() => {
  const q = varSearch.value.toLowerCase();
  if (!q) return availableVariables.value;
  return availableVariables.value.filter(v => resolveLabel(v.label).toLowerCase().includes(q) || v.key.toLowerCase().includes(q));
});

function openPicker(field) {
  activeField.value = field;
  varSearch.value = '';
}

function closePicker() {
  activeField.value = null;
  varSearch.value = '';
}

function selectVar(key) {
  if (activeField.value) {
    const current = props.modelValue?.[activeField.value] ?? '';
    update({ [activeField.value]: current + `{{${key}}}` });
  }
  closePicker();
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- To -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SEND_EMAIL_CONTACT.TO') }}</span>
      <div v-on-click-outside="() => activeField === 'to' && closePicker()" class="relative">
        <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all">
          <input
            class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-sm !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
            :value="modelValue?.to ?? ''"
            :placeholder="t('FLOW_BUILDER.ACTION_FORMS.SEND_EMAIL_CONTACT.TO_PLACEHOLDER')"
            @input="update({ to: $event.target.value })"
          />
          <button
            type="button"
            class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
            :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
            @click.stop="openPicker('to')"
          >
            <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
          </button>
        </div>
        <div
          v-if="activeField === 'to'"
          class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
        >
          <div class="px-2 pt-2 pb-1">
            <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
              <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
              <input v-model="varSearch" type="text" class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9" :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')" autofocus />
            </div>
          </div>
          <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
            <button v-for="v in filteredVars" :key="v.key" type="button" class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors" @click="selectVar(v.key)">{{ resolveLabel(v.label) }}</button>
            <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Subject -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SEND_EMAIL_CONTACT.SUBJECT') }}</span>
      <div v-on-click-outside="() => activeField === 'subject' && closePicker()" class="relative">
        <div class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all">
          <input
            class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-sm !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
            :value="modelValue?.subject ?? ''"
            :placeholder="t('FLOW_BUILDER.ACTION_FORMS.SEND_EMAIL_CONTACT.SUBJECT_PLACEHOLDER')"
            @input="update({ subject: $event.target.value })"
          />
          <button
            type="button"
            class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
            :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
            @click.stop="openPicker('subject')"
          >
            <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
          </button>
        </div>
        <div
          v-if="activeField === 'subject'"
          class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
        >
          <div class="px-2 pt-2 pb-1">
            <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
              <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
              <input v-model="varSearch" type="text" class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9" :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')" autofocus />
            </div>
          </div>
          <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
            <button v-for="v in filteredVars" :key="v.key" type="button" class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors" @click="selectVar(v.key)">{{ resolveLabel(v.label) }}</button>
            <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Body -->
    <div class="flex flex-col gap-1.5">
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SEND_EMAIL_CONTACT.BODY') }}</span>
        <button
          type="button"
          class="text-xs text-n-slate-10 hover:text-n-slate-12 flex items-center gap-1"
          @click="openPicker('body')"
        >
          <span class="i-ph-brackets-curly size-3.5" />
          {{ t('FLOW_BUILDER.ACTION_FORMS.SEND_EMAIL_CONTACT.VARIABLE') }}
        </button>
      </div>
      <div v-on-click-outside="() => activeField === 'body' && closePicker()" class="relative">
        <textarea
          rows="5"
          class="reset-base w-full px-3 py-2 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none transition-all"
          :value="modelValue?.body ?? ''"
          :placeholder="t('FLOW_BUILDER.ACTION_FORMS.SEND_EMAIL_CONTACT.BODY_PLACEHOLDER')"
          @input="update({ body: $event.target.value })"
        />
        <div
          v-if="activeField === 'body'"
          class="absolute top-0 right-0 mt-1 mr-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-[200px] overflow-hidden"
        >
          <div class="px-2 pt-2 pb-1">
            <div class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand">
              <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
              <input v-model="varSearch" type="text" class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9" :placeholder="t('FLOW_BUILDER.COMMON.SEARCH_VARIABLE')" autofocus />
            </div>
          </div>
          <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
            <button v-for="v in filteredVars" :key="v.key" type="button" class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors" @click="selectVar(v.key)">{{ resolveLabel(v.label) }}</button>
            <p v-if="!filteredVars.length" class="px-3 py-2 text-xs text-n-slate-9 italic">{{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
